import { Box, VStack } from "@chakra-ui/react"
import useIntersection from "@pathwright/ui/src/components/hooks/useIntersection"
import { useScrollContext } from "@pathwright/ui/src/components/scroll/ScrollView"
import PropTypes from "prop-types"
import React, { useCallback, useEffect } from "react"
import { useDiscussionListContext } from "./DiscussionListContainer"
import DiscussionListItem from "./item/DiscussionListItem"
import DiscussionListItemLoading from "./item/DiscussionListItemLoading"
import { invalidateDiscussionCache } from "./utils"

// NOTE: useDiscussionSyncer is not efficent in that it does not update the cache (in most cases),
// but instead refetches the discussions query. Opted for this behavior as handling certain changes
// by updating the cache can get really messy (i.e. changing is_discussion_question should move the discussion
// to a different query)
const useDiscussionSyncer = () => {
  const { context, sort, filter, tagIdFilter, discussionsQuery } =
    useDiscussionListContext()

  const handleDiscussionCreated = useCallback(
    (discussion) => {
      invalidateDiscussionCache(context)
      discussionsQuery.refetch()
    },
    [context]
  )

  const handleDiscussionDeleted = useCallback(
    (discussion) => {
      invalidateDiscussionCache(context)
      discussionsQuery.refetch()
      // deleting discussion can be handled via updating the cache since we're simply removing
      // the discussion
      // deleteDiscussionInDiscussionsCache({
      //   context,
      //   tagIdFilter,
      //   filter,
      //   sort
      // })(discussion)
    },
    [context, tagIdFilter, filter, sort]
  )

  useEffect(() => {
    document.addEventListener("discussion:created", handleDiscussionCreated)
    document.addEventListener("discussion:deleted", handleDiscussionDeleted)

    return () => {
      document.removeEventListener(
        "discussion:created",
        handleDiscussionCreated
      )
      document.removeEventListener(
        "discussion:deleted",
        handleDiscussionDeleted
      )
    }
  }, [handleDiscussionCreated, handleDiscussionDeleted])
}

const DiscussionList = ({ onStartDiscussion, inverted, children }) => {
  const {
    context,
    discussions,
    loading,
    loadMore,
    hasMore,
    loadingMore,
    loadingCount,
    onSelectDiscussion,
    onSelectDiscussionTag,
    onFilterTags,
    setListNode,
    showReplyPrompt,
    filterBySelectedDiscussionTag
  } = useDiscussionListContext()

  const scrollContext = useScrollContext()
  // we'll check if the LoadingBlock placeholder element has entered the containing ScrollView
  const [setIntersectionNode, intersection, hasIntersected] = useIntersection({
    root: scrollContext && scrollContext.scrollNode
  })

  useDiscussionSyncer()

  // paginate when last node
  React.useEffect(() => {
    if (hasIntersected && !loading && !loadingMore && hasMore) loadMore()
  }, [hasIntersected])

  const handleSelectDiscussion = (discussion, viaReplyPrompt = false) =>
    onSelectDiscussion(discussion, {
      view: "show",
      viaReplyPrompt
    })

  const handleEditDiscussion = (discussion) =>
    onSelectDiscussion(discussion, {
      view: "update"
    })

  const handleSelectTag = filterBySelectedDiscussionTag
    ? (tag) => onFilterTags([tag])
    : null

  return (
    <Box className="DiscussionList" ref={setListNode} w="100%">
      <VStack w="100%" spacing={4}>
        {Boolean(discussions && !discussions.length && children) &&
          children({ inverted })}
        {!!discussions && (
          <>
            {discussions.map((discussion, i) => (
              <Box
                key={discussion.id}
                ref={i === discussions.length - 1 ? setIntersectionNode : null}
                w="100%"
              >
                <DiscussionListItem
                  key={discussion.id}
                  context={context}
                  discussion={discussion}
                  showReplyPrompt={showReplyPrompt}
                  onSelectDiscussion={handleSelectDiscussion}
                  onEditDiscussion={handleEditDiscussion}
                  onSelectTag={handleSelectTag}
                />
              </Box>
            ))}
          </>
        )}
        {loadingCount > 0 && (
          <>
            {new Array(loadingCount).fill().map((_, i) => (
              <Box key={i} w="100%">
                <DiscussionListItemLoading />
              </Box>
            ))}
          </>
        )}
      </VStack>
    </Box>
  )
}

DiscussionList.displayName = "DiscussionList"

DiscussionList.defaultProps = {
  onSelectDiscussion: () => {}
}

DiscussionList.propTypes = {
  children: PropTypes.func,
  inverted: PropTypes.bool
}

export default DiscussionList
